import MoreVertIcon from "@mui/icons-material/MoreVert";
import {
  Box,
  Chip,
  CircularProgress,
  IconButton,
  Menu,
  MenuItem,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
  Tooltip,
  Typography,
} from "@mui/material";
import React, { useMemo, useState } from "react";
import { GetPracticesListSortOrderEnum } from "libs/cellera-sdk";

export interface Practice {
  id: string;
  name: string;
  location: string;
  specialties: string[];
}

interface PracticeTableProps {
  practices: Practice[];
  loading: boolean;
  sortBy: string;
  sortOrder: GetPracticesListSortOrderEnum;
  onSortChange: (
    sortBy: string,
    sortOrder: GetPracticesListSortOrderEnum,
  ) => void;
  onPracticeClick: (practiceId: string) => void;
  pagination: {
    page: number;
    pageSize: number;
    total: number;
    onPageChange: (page: number) => void;
    onPageSizeChange: (pageSize: number) => void;
  };
}

const PracticeTable: React.FC<PracticeTableProps> = ({
  practices,
  loading,
  sortBy,
  sortOrder,
  onSortChange,
  onPracticeClick,
  pagination,
}) => {
  const columns = useMemo(
    () => [
      { id: "name", label: "Practice Name", sortable: true },
      { id: "location", label: "Location", sortable: true },
      { id: "specialties", label: "Specialties", sortable: false },
      { id: "actions", label: "", sortable: false },
    ],
    [],
  );

  const renderSpecialties = (specialties: string[]) => {
    if (!specialties.length) return "-";
    const maxDisplay = 3;
    const visibleSpecialties = specialties.slice(0, maxDisplay);
    const remainingCount = specialties.length - maxDisplay;

    return (
      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
        {visibleSpecialties.map((specialty) => (
          <Chip
            key={specialty}
            label={specialty}
            size="small"
            sx={{
              backgroundColor: "#DBEAFE",
              color: "#1E3A8A",
              "& .MuiChip-label": { fontSize: "0.75rem", px: 1 },
            }}
          />
        ))}
        {remainingCount > 0 && (
          <Tooltip
            arrow
            placement="top"
            title={
              <Box>
                <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
                  All Specialties:
                </Typography>
                {specialties.map((specialty) => (
                  <Typography key={specialty} variant="body2">
                    • {specialty}
                  </Typography>
                ))}
              </Box>
            }
            slotProps={{
              tooltip: {
                sx: {
                  backgroundColor: "#ffffff",
                  color: "#374151",
                  border: "1px solid #E5E7EB",
                  boxShadow: "0 4px 12px rgba(0,0,0,0.08)",
                  "& .MuiTooltip-arrow": {
                    color: "#ffffff",
                    "&::before": { border: "1px solid #E5E7EB" },
                  },
                },
              },
            }}
          >
            <Chip
              label={`(+${remainingCount} more)`}
              size="small"
              sx={{
                backgroundColor: "#E5E7EB",
                color: "#6B7280",
                "& .MuiChip-label": { fontSize: "0.75rem", px: 1 },
              }}
            />
          </Tooltip>
        )}
      </Box>
    );
  };

  const handleSort = (column: string) => {
    const nextOrder =
      sortBy === column && sortOrder === GetPracticesListSortOrderEnum.Asc
        ? GetPracticesListSortOrderEnum.Desc
        : GetPracticesListSortOrderEnum.Asc;
    onSortChange(column, nextOrder);
  };

  if (loading && practices.length === 0) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Paper
      sx={{ border: "1px solid #E5E7EB", borderRadius: 2, overflow: "hidden" }}
    >
      <TableContainer>
        <Table>
          <TableHead>
            <TableRow sx={{ backgroundColor: "#F9FAFB" }}>
              {columns.map((column) => (
                <TableCell
                  key={column.id}
                  sx={{
                    borderBottom: "1px solid #E5E7EB",
                    fontWeight: 600,
                    color: "#111827",
                  }}
                >
                  {column.sortable ? (
                    <TableSortLabel
                      active={sortBy === column.id}
                      direction={
                        sortBy === column.id
                          ? sortOrder === GetPracticesListSortOrderEnum.Asc
                            ? "asc"
                            : "desc"
                          : "asc"
                      }
                      onClick={() => handleSort(column.id)}
                    >
                      {column.label}
                    </TableSortLabel>
                  ) : (
                    column.label
                  )}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>

          <TableBody>
            {practices.length === 0 && (
              <TableRow>
                <TableCell
                  colSpan={columns.length}
                  align="center"
                  sx={{ py: 6 }}
                >
                  No practices found
                </TableCell>
              </TableRow>
            )}

            {practices.map((practice) => (
              <TableRow
                key={practice.id}
                hover
                sx={{
                  cursor: "pointer",
                  "&:hover": { backgroundColor: "#F3F4F6" },
                }}
                onClick={() => onPracticeClick(practice.id)}
              >
                <TableCell sx={{ fontWeight: 500 }}>{practice.name}</TableCell>
                <TableCell>{practice.location}</TableCell>
                <TableCell>{renderSpecialties(practice.specialties)}</TableCell>
                <TableCell align="right" onClick={(e) => e.stopPropagation()}>
                  <RowActions
                    onViewDetails={() => onPracticeClick(practice.id)}
                  />
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <TablePagination
        component="div"
        count={pagination.total}
        page={pagination.page - 1}
        onPageChange={(_, newPage) => pagination.onPageChange(newPage + 1)}
        rowsPerPage={pagination.pageSize}
        onRowsPerPageChange={(event) =>
          pagination.onPageSizeChange(parseInt(event.target.value, 10))
        }
        rowsPerPageOptions={[10, 20, 50]}
      />
    </Paper>
  );
};

type RowActionsProps = {
  onViewDetails: () => void;
};

const RowActions: React.FC<RowActionsProps> = ({ onViewDetails }) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  const handleOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => setAnchorEl(null);

  const handleView = () => {
    handleClose();
    onViewDetails();
  };

  return (
    <>
      <IconButton size="small" onClick={handleOpen}>
        <MoreVertIcon fontSize="small" />
      </IconButton>
      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <MenuItem onClick={handleView}>View details</MenuItem>
      </Menu>
    </>
  );
};

export default PracticeTable;
